/* import __COLOCATED_TEMPLATE__ from './text-formatter.hbs.js'; */
var _class, _descriptor, _class2;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { createAnchor } from '@intercom/composer-ember/lib/commands/anchor';
import { generateHeadingId } from '@intercom/composer-core';
import { INLINE_CONTROL_ALIGNMENT, INLINE_CONTROL_BLOCK_TYPES } from '@intercom/composer-ember/lib/config/composer-config';
import { ALIGNMENTS, TEXT_FORMATTER_MARK_OPTIONS } from '@intercom/composer-ember/lib/blocks/constants';
import { ColorPickerType } from '../utils/constants';
import { tracked } from '@glimmer/tracking';
import { findParentNodeOfType } from 'prosemirror-utils';
/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
// @ts-ignore
import { cached } from 'tracked-toolbox';
export let Formatter = (_class = class Formatter {
  constructor(config, commands) {
    _initializerDefineProperty(this, "loading", _descriptor, this);
    _defineProperty(this, "config", void 0);
    _defineProperty(this, "commands", void 0);
    this.config = config;
    this.commands = commands;
  }
  get hasTransformFn() {
    return 'transform' in this.config && typeof this.config.transform === 'function';
  }
  get hasCustomComponent() {
    return 'componentName' in this.config && typeof this.config.componentName === 'string';
  }
  async format(transformFn) {
    this.loading = true;
    try {
      return await this.commands.transformSelection(transformFn);
    } catch (e) {
      if (e instanceof RangeError) {
        // user typed into composer while transform was running, ignore
      } else {
        throw e;
      }
    } finally {
      this.loading = false;
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "loading", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "format", [action], Object.getOwnPropertyDescriptor(_class.prototype, "format"), _class.prototype), _class);
let TextFormatter = (_class2 = class TextFormatter extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "colorPickerType", ColorPickerType.TEXT);
  }
  get customFormatters() {
    return this.args.composer.config.formatters?.config.map(config => new Formatter(config, this.args.composer.commands)) ?? [];
  }
  get hasCustomFormatters() {
    return this.customFormatters.length > 0;
  }
  get customFormattersPlacement() {
    return this.args.composer.config.formatters?.placement;
  }
  get composer() {
    return this.args.composer;
  }
  get state() {
    return this.composer.state;
  }
  get schema() {
    return this.composer.schema;
  }
  get isVisible() {
    return this.composer.state.hasSelectedText && this.composer.state.isFormattable;
  }
  get isSelectionEntirelyInCodeBlock() {
    let selection = this.composer.state.prosemirrorState.selection;
    let $head = selection.$head;
    let $anchor = selection.$anchor;
    if (!$head.sameParent($anchor)) {
      return false;
    }
    return $head.parent.type === this.schema.nodes.codeBlock;
  }
  get isSelectionEntirelyInCollapsibleSectionSummary() {
    let selection = this.composer.state.prosemirrorState.selection;
    let $head = selection.$head;
    let $anchor = selection.$anchor;
    let collapsibleSection = findParentNodeOfType(this.schema.nodes.collapsibleSectionSummary)(selection);
    if (!$head.sameParent($anchor)) {
      return false;
    }
    return !!collapsibleSection;
  }
  get selectionHasBold() {
    return this.composer.state.selectionHasMark('bold');
  }
  get selectionHasItalic() {
    return this.composer.state.selectionHasMark('italic');
  }
  get selectionHasCode() {
    return this.composer.state.selectionHasMark('code');
  }
  get selectionHasAnchor() {
    return this.composer.state.selectionHasMark('anchor');
  }
  get selectionIsInSingleHeading() {
    return this.isSingleBlockSelection && this.composer.state.selectionAnchorNodeType === this.schema?.nodes.heading;
  }
  get selectionIsInSingleSubheading() {
    return this.isSingleBlockSelection && this.composer.state.selectionAnchorNodeType === this.schema?.nodes.subheading;
  }
  get selectionIsInSingleSubheading3() {
    return this.isSingleBlockSelection && this.composer.state.selectionAnchorNodeType === this.schema?.nodes.subheading3;
  }
  get selectionIsInSingleSubheading4() {
    return this.isSingleBlockSelection && this.composer.state.selectionAnchorNodeType === this.schema?.nodes.subheading4;
  }
  get selectionIsInSingleSmall() {
    return this.isSingleBlockSelection && this.composer.state.selectionAnchorNodeType === this.schema?.nodes.small;
  }
  get isSingleBlockSelection() {
    return this.composer.state.selectionIsInSingleBlock;
  }
  get isTextDirectionLeftToRight() {
    return this.composer.config.textDirection === 'ltr' || this.composer.config.textDirection === 'auto';
  }
  get isTextAlignmentAllowed() {
    return this.composer.config.allowTextAlignment;
  }
  get shouldShowAllTextAlignments() {
    return this.composer.config.allowTextAlignment && this.composer.config.showAllTextAlignment;
  }
  get hideFromTextFormatterConfig() {
    return this.composer.config.hideFromTextFormatter || [];
  }
  get showBlockTypes() {
    return !this.hideFromTextFormatterConfig.includes(INLINE_CONTROL_BLOCK_TYPES);
  }
  get showCustomizations() {
    return this.composer.config.customStylesConfig?.allowFontFamilyInTextFormatter || this.composer.config.customStylesConfig?.colorPickerComponent;
  }
  get showAlignments() {
    return !this.hideFromTextFormatterConfig.includes(INLINE_CONTROL_ALIGNMENT);
  }
  get showTextAlignmentButtons() {
    return this.isTextAlignmentAllowed && this.showAlignments && !this.isSelectionEntirelyInCollapsibleSectionSummary;
  }
  get anyMarkOptions() {
    let marks = this.schema.marks;
    return TEXT_FORMATTER_MARK_OPTIONS.some(mark => Object.keys(marks).includes(mark));
  }
  get isTextAlignmentLeading() {
    return this.isTextDirectionLeftToRight ? this.state.dominantAlignmentIsLeft : this.state.dominantAlignmentIsRight;
  }
  get isTextAlignmentCenter() {
    return this.state.dominantAlignmentIsCenter;
  }
  get isTextAlignmentTrailing() {
    return this.isTextDirectionLeftToRight ? this.state.dominantAlignmentIsRight : this.state.dominantAlignmentIsLeft;
  }
  get isTextAlignmentJustified() {
    return this.state.dominantAlignmentIsJustified;
  }
  toggleBold() {
    if (this.schema?.marks.bold) {
      this.composer.commands.toggleMarkType('bold');
      this.composer.commands.focus();
    }
  }
  toggleItalic() {
    if (this.schema?.marks.italic) {
      this.composer.commands.toggleMarkType('italic');
      this.composer.commands.focus();
    }
  }
  toggleCode() {
    if (this.schema?.marks.code) {
      this.composer.commands.toggleMarkType('code');
      this.composer.commands.focus();
    }
  }
  toggleHeading(headingType) {
    this.composer.commands.toggleNodeType(headingType, {
      idAttribute: generateHeadingId()
    });
    this.composer.commands.focus();
  }
  toggleH1() {
    this.toggleHeading('heading');
  }
  toggleH2() {
    this.toggleHeading('subheading');
  }
  toggleH3() {
    this.toggleHeading('subheading3');
  }
  toggleH4() {
    this.toggleHeading('subheading4');
  }
  toggleSmall() {
    this.composer.commands.toggleNodeType('small');
    this.composer.commands.focus();
  }
  toggleCodeBlock() {
    this.composer.commands.toggleNodeType('codeBlock');
    this.composer.commands.focus();
  }
  createAnchor() {
    if (this.state) {
      createAnchor(this.state.prosemirrorState, this.composer.commands.dispatch);
    }
  }
  alignDefault() {
    if (this.state?.dominantAlignmentIsCenter) {
      this.composer.commands.toggleCenter();
      this.composer.commands.focus();
    }
  }
  alignLeading() {
    if (this.isTextAlignmentLeading) {
      return;
    }
    let alignment = this.isTextDirectionLeftToRight ? ALIGNMENTS.left : ALIGNMENTS.right;
    this.composer.commands.setTextAligment(alignment);
    this.composer.commands.focus();
  }
  alignCenter() {
    if (this.isTextAlignmentCenter) {
      return;
    }
    this.composer.commands.setTextAligment(ALIGNMENTS.center);
    this.composer.commands.focus();
  }
  toggleCenter() {
    if (!this.isTextAlignmentCenter) {
      this.composer.commands.toggleCenter();
      this.composer.commands.focus();
    }
  }
  alignTrailing() {
    if (this.isTextAlignmentTrailing) {
      return;
    }
    let alignment = this.isTextDirectionLeftToRight ? ALIGNMENTS.right : ALIGNMENTS.left;
    this.composer.commands.setTextAligment(alignment);
    this.composer.commands.focus();
  }
  alignJustify() {
    if (this.isTextAlignmentJustified) {
      return;
    }
    this.composer.commands.setTextAligment(ALIGNMENTS.justify);
    this.composer.commands.focus();
  }
}, _applyDecoratedDescriptor(_class2.prototype, "customFormatters", [cached], Object.getOwnPropertyDescriptor(_class2.prototype, "customFormatters"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleBold", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleBold"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleItalic", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleItalic"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleCode", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleCode"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleH1", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleH1"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleH2", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleH2"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleH3", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleH3"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleH4", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleH4"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleSmall", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleSmall"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleCodeBlock", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleCodeBlock"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "createAnchor", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "createAnchor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "alignDefault", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "alignDefault"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "alignLeading", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "alignLeading"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "alignCenter", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "alignCenter"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleCenter", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleCenter"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "alignTrailing", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "alignTrailing"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "alignJustify", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "alignJustify"), _class2.prototype), _class2);
export { TextFormatter as default };